import React, { useState, useEffect, createContext } from "react";
import "./App.css";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import BottomNavigation from "./components/BottomNavigation";
import SplashScreen from "./components/SplashScreen";
import PageTransition from "./components/PageTransition";
import { NavigationProvider } from "./context/NavigationContext";
import { AuthProvider, useAuth } from "./context/AuthContext";

// 페이지 컴포넌트 임포트
import HomePage from "./pages/HomePage";
import TalkPage from "./pages/TalkPage";
import MapPage from "./pages/MapPage";
import MapSearchPage from "./pages/MapSearchPage";
import ChatPage from "./pages/ChatPage";
import MyPage from "./pages/MyPage";
import FeedDetailPage from "./pages/FeedDetailPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import CreateFeedPage from "./pages/CreateFeedPage";

// 스플래시 상태 컨텍스트 생성
export const SplashContext = createContext<{
  splashComplete: boolean;
}>({
  splashComplete: false,
});

// 로딩 컴포넌트
const LoadingScreen: React.FC = () => (
  <div className="loading-screen">
    <div className="loading-spinner"></div>
  </div>
);

// 바텀 네비게이션 표시 여부 확인 함수
const shouldShowBottomNav = (pathname: string) => {
  // 특정 경로에서 바텀 네비게이션을 숨깁니다
  return !(
    (pathname.startsWith("/chat/") && pathname !== "/chat/") ||
    pathname.startsWith("/feed/") ||
    pathname.startsWith("/map/search") ||
    pathname === "/login" ||
    pathname === "/register"
  );
};

// PC 사이드바 컴포넌트
const SideBox = () => {
  const backgroundStyle = {
    backgroundImage: `url(${process.env.PUBLIC_URL}/images/assets/background.png)`,
    backgroundSize: "cover",
    backgroundPosition: "center center",
    backgroundRepeat: "no-repeat",
  };

  return (
    <div className="side-box" style={backgroundStyle}>
      <div className="side-box-content">
        <div className="logo-section">
          <img
            src={process.env.PUBLIC_URL + "/images/icons/main-logo.png"}
            alt="logo"
            className="side-logo"
          />
        </div>

        <div className="main-section">
          <h2 className="main-title">
            가장 쉽고 빠른{" "}
            <span className="highlight">반려동물 소통의 시작, 펫밀리</span>
            <br />
            지금 바로 경험해 보세요!
          </h2>

          <div className="character-section">
            <div className="character-illustration">
              <img
                className="character-illustration-image"
                src={process.env.PUBLIC_URL + "/images/assets/main-info.png"}
                alt="character"
              />
            </div>
          </div>
        </div>

        <div className="qr-section">
          <div className="qr-code">
            <img
              className="qr-code-image"
              src={process.env.PUBLIC_URL + "/images/sample/qr-code.png"}
              alt="qr-code"
            />
            <div className="qr-description">
              <p className="qr-main-text">
                앱을 통해 더 다양한 소식 만나러 가기
              </p>
              <p className="qr-sub-text">← 앱 다운로드</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// 라우트 컴포넌트
const AppRoutes = () => {
  const location = useLocation();
  const { loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <PageTransition>
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<HomePage />} />
        <Route path="/talk" element={<TalkPage />} />
        <Route path="/map" element={<MapPage />} />
        <Route path="/map/search" element={<MapSearchPage />} />
        <Route path="/chat/*" element={<ChatPage />} />
        <Route path="/mypage" element={<MyPage />} />
        <Route path="/feed/:feedId" element={<FeedDetailPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/create-feed" element={<CreateFeedPage />} />
      </Routes>
    </PageTransition>
  );
};

// 바텀 네비게이션 래퍼 컴포넌트
const BottomNavigationWrapper = () => {
  const location = useLocation();

  if (!shouldShowBottomNav(location.pathname)) {
    return null; // 특정 경로에서는 바텀 네비게이션을 렌더링하지 않음
  }

  return <BottomNavigation />;
};

function App() {
  const [showSplash, setShowSplash] = useState(true);
  const [splashComplete, setSplashComplete] = useState(false);
  const [appReady, setAppReady] = useState(false);

  // 앱 컴포넌트가 미리 로드되도록 설정
  useEffect(() => {
    setAppReady(true);
  }, []);

  const handleSplashComplete = () => {
    setShowSplash(false);
    setSplashComplete(true);
  };

  return (
    <>
      {showSplash && <SplashScreen onComplete={handleSplashComplete} />}

      <div
        style={{
          display: showSplash ? "none" : "block",
        }}
        className="main-content"
      >
        <SplashContext.Provider value={{ splashComplete }}>
          <AuthProvider>
            <Router>
              <NavigationProvider>
                <div className="desktop-layout">
                  <SideBox />
                  <div className="App">
                    <main className="App-content">
                      <AppRoutes />
                    </main>
                    <BottomNavigationWrapper />
                  </div>
                </div>
              </NavigationProvider>
            </Router>
          </AuthProvider>
        </SplashContext.Provider>
      </div>
    </>
  );
}

export default App;
